import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** Key the admin's own token is parked under while viewing as someone else. */
export const SWITCH_USER_ADMIN_TOKEN_KEY = 'switchUserAdminToken';

export interface SwitchUserStatus {
  can_switch: boolean;
  switched_from: { id: number; name: string } | null;
}

export interface SwitchableUser {
  id: number;
  name: string;
  email: string;
  role: string | null;
  events: { id: number; reference: string | null; status: string }[];
}

/**
 * TVG-378: pre-production-only "switch user". The API 404s everywhere the QA
 * guard says no, which this service reads as "not available" so the control
 * and banner simply never render on production.
 */
@Injectable({
  providedIn: 'root',
})
export class SwitchUserService {
  private readonly baseUrl = `${environment.apiUrl}/api/qa/switch-user`;
  private readonly statusSubject = new BehaviorSubject<SwitchUserStatus | null>(null);

  readonly status$ = this.statusSubject.asObservable();

  constructor(private http: HttpClient) {}

  isSwitched(): boolean {
    return !!localStorage.getItem(SWITCH_USER_ADMIN_TOKEN_KEY);
  }

  /**
   * Only asks the API when it could matter (an admin, or a parked admin token),
   * so customers and staff on production never call the route at all.
   */
  loadStatus(roles: string[]): void {
    if (!this.isSwitched() && !roles.includes('admin')) {
      this.statusSubject.next(null);
      return;
    }

    this.http
      .get<{ data: SwitchUserStatus }>(`${this.baseUrl}/status`)
      .pipe(
        map((response) => response.data),
        catchError(() => of(null)),
      )
      .subscribe((status) => this.statusSubject.next(status));
  }

  users(): Observable<SwitchableUser[]> {
    return this.http.get<{ data: SwitchableUser[] }>(`${this.baseUrl}/users`).pipe(map((response) => response.data));
  }

  /**
   * @param landingUrl where to open as the chosen user; defaults to their home.
   */
  switchTo(user: SwitchableUser, landingUrl?: string): Observable<unknown> {
    return this.http.post<{ data: { token: string } }>(`${this.baseUrl}/users/${user.id}`, {}).pipe(
      tap((response) => this.adopt(response.data.token, landingUrl ?? (user.role === 'customer' ? '/my-home' : '/events'))),
    );
  }

  /**
   * Parks the admin's own token and reloads as the switched-to user. A full
   * reload drops every cached view of the admin (user, toggles, sidebar) so
   * the screens are built from scratch as the chosen user.
   */
  private adopt(switchToken: string, url: string): void {
    const adminToken = localStorage.getItem('token');
    if (adminToken) {
      localStorage.setItem(SWITCH_USER_ADMIN_TOKEN_KEY, adminToken);
    }
    localStorage.setItem('token', switchToken);
    window.location.href = url;
  }

  switchBack(): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/back`, {}).pipe(
      catchError(() => of(null)),
      tap(() => this.restoreAdminSession()),
    );
  }

  restoreAdminSession(): void {
    restoreSwitchUserAdminSession();
  }
}

/**
 * Puts the admin's own token back and reloads into the admin screens. A plain
 * function so the auth interceptor can call it without injecting HttpClient.
 */
export function restoreSwitchUserAdminSession(): void {
  const adminToken = localStorage.getItem(SWITCH_USER_ADMIN_TOKEN_KEY);
  localStorage.removeItem(SWITCH_USER_ADMIN_TOKEN_KEY);
  if (adminToken) {
    localStorage.setItem('token', adminToken);
    window.location.href = '/events';
  } else {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }
}
